import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useOfficialTerms, disciplineLabel, DISCIPLINE_EMOJI } from "../lib/officialTerms";
import { SafetyScoringSection } from "../components/jm/SafetyScoringSection";
import { SuspensionNotice } from "../components/jm/SuspensionNotice";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DISCIPLINE_LABEL = {
  muay_thai_educatif: "🥊 Kickboxing/Muaythai",
  kickboxing_educatif: "🥊 Kickboxing/Muaythai",
  jjb: "🥋 Jiu-Jitsu Brésilien",
  can_ryu_demo: "🇯🇵 Jiu-jitsu Can Ryu (démo)",
  petits_samourais: "🐣 Petits Samouraïs — parcours (3-6 ans)",
};

const BELT_OPTIONS = {
  jjb: ["blanche", "grise", "jaune", "orange", "verte", "bleue", "mauve", "brune", "noire"],
  muay_thai_educatif: ["blanche", "jaune", "orange", "verte", "bleue", "mauve", "brune", "noire", "mongkhon"],
  kickboxing_educatif: ["blanche", "jaune", "orange", "verte", "bleue", "mauve", "brune", "noire"],
  can_ryu_demo: ["blanche", "jaune", "orange", "verte", "bleue", "mauve", "brune", "noire"],
  petits_samourais: ["petit_samourai"],
};

export default function CompetitionPublicRegister() {
  const OT = useOfficialTerms();
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [refSchools, setRefSchools] = useState([]);
  const [form, setForm] = useState({
    athlete_first_name: "", athlete_last_name: "", athlete_dob: "", athlete_gender: "",
    athlete_weight_kg: "", school_name: "", school_city: "", coach_name: "",
    entries: [],
    parent_name: "", parent_email: "", parent_phone: "",
    waiver_accepted: false, waiver_signature_text: "",
    medical_notes: "", emergency_contact: "",
  });

  useEffect(() => {
    axios.get(`${API}/mini-competitions/public/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
    axios.get(`${API}/jm/public/${slug}/schools`)
      .then((r) => setRefSchools(r.data.schools || []))
      .catch(() => setRefSchools([]));
  }, [slug]);

  const toggleEntry = (discipline) => {
    setForm((f) => {
      const exists = f.entries.find((e) => e.discipline === discipline);
      if (exists) {
        return { ...f, entries: f.entries.filter((e) => e.discipline !== discipline) };
      }
      return { ...f, entries: [...f.entries, { discipline, belt_level: (BELT_OPTIONS[discipline] || ["blanche"])[0], experience_years: "0" }] };
    });
  };

  const setBelt = (discipline, belt) => {
    setForm((f) => ({
      ...f,
      entries: f.entries.map((e) => e.discipline === discipline ? { ...e, belt_level: belt } : e),
    }));
  };

  const submit = async () => {
    if (!form.waiver_accepted || !form.waiver_signature_text.trim()) {
      toast.error("Tu dois accepter et signer la décharge de responsabilité.");
      return;
    }
    if (form.entries.length === 0) {
      toast.error("Choisis au moins 1 discipline.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        athlete_weight_kg: form.athlete_weight_kg ? Number(form.athlete_weight_kg) : null,
      };
      const r = await axios.post(`${API}/mini-competitions/public/${slug}/register`, payload);
      if (r.data?.checkout_url) {
        toast.success("Redirection vers le paiement sécurisé…");
        window.location.href = r.data.checkout_url;
        return;
      }
      setConfirmation(r.data);
      toast.success("Inscription enregistrée 🥋");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'inscription");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center text-sm">Chargement…</div>;
  }
  if (!event) {
    return (
      <div className="min-h-screen bg-black text-zinc-400 flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-amber-400 text-xs tracking-[0.4em] mb-3">KAIZEN COMPÉTITIONS</div>
          <h1 className="text-3xl font-bold text-zinc-100 mb-3">Compétition introuvable</h1>
          <p className="text-zinc-500 text-sm">Le lien n'est pas valide ou les inscriptions sont fermées.</p>
        </div>
      </div>
    );
  }

  if (confirmation) {
    return (
      <div className="min-h-screen bg-black text-zinc-200 px-6 py-12" data-testid="registration-confirmation">
        <div className="max-w-2xl mx-auto">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">INSCRIPTION CONFIRMÉE</div>
          <h1 className="text-4xl font-bold tracking-tight mb-4">Bienvenue {confirmation.athlete} 🥋</h1>
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-2xl p-6 mt-6">
            <div className="text-sm text-emerald-300 mb-3">✅ Ton inscription à <strong>{event.name}</strong> est enregistrée.</div>
            <div className="space-y-2 text-sm text-zinc-300">
              <div>📅 Date : <strong>{event.date}</strong></div>
              {event.location && <div>📍 Lieu : <strong>{event.location}</strong></div>}
              <div>🎂 Âge au jour J : <strong>{confirmation.age_at_event} ans</strong></div>
              <div>💵 Montant dû au check-in : <strong>{confirmation.amount_due_cad.toFixed(2)} $ CAD</strong></div>
              <div className="text-zinc-500 mt-2 text-xs">{confirmation.message}</div>
            </div>
          </div>
          <div className="mt-8 text-xs text-zinc-600 italic text-center">
            Un email de confirmation suivra. Pour toute question : {event.organizer_email || "contacte ton dojo"}.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-zinc-200" data-testid="competition-public-register">
      {/* Hero */}
      <div className="border-b border-zinc-900 bg-gradient-to-br from-zinc-950 via-black to-zinc-950">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-3">{(OT.network_name || "Réseau Kaizen").toUpperCase()} · {(OT.event_title || "Jeux Martiaux Inter-Écoles").toUpperCase()}</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="event-name">{event.name}</h1>
          <div className="text-zinc-500 text-xs mt-2" data-testid="event-signature">{OT.event_signature}</div>
          <div className="text-zinc-400 mt-3 text-lg">📅 {event.date}{event.location && ` · 📍 ${event.location}`}</div>
          {event.address && (
            <a href={`https://maps.google.com/?q=${encodeURIComponent(event.address)}`} target="_blank" rel="noreferrer"
              className="inline-block text-amber-300 hover:text-amber-200 text-sm mt-1 underline underline-offset-2" data-testid="public-event-address">
              {event.address} · Itinéraire
            </a>
          )}
          <div className="flex gap-2 mt-4 flex-wrap">
            {event.disciplines.map((d) => (
              <Badge key={d} variant="outline" className="border-amber-500/40 text-amber-300">{DISCIPLINE_EMOJI[d] || ""} {disciplineLabel(d, OT)}</Badge>
            ))}
            {!event.suspended && <Badge variant="outline" className="border-zinc-700 text-zinc-400">Frais : 30 $ (1 discipline) · +10 $ / discipline supp. · paiement en ligne</Badge>}
          </div>
          {event.description && <p className="text-zinc-400 mt-4">{event.description}</p>}
        </div>
      </div>

      {event.suspended ? (
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
          <SuspensionNotice event={event} />
          <SafetyScoringSection compact />
        </div>
      ) : (
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <SafetyScoringSection compact />
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/mini-competitions/public/${slug}/consentement.pdf`} target="_blank" rel="noreferrer" className="block rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200 hover:bg-amber-500/10" data-testid="public-consent-pdf-link">
          📄 Athlète mineur : télécharger le formulaire de consentement parental et déclaration médicale (PDF) — à remettre signé avant la participation.
        </a>
        {/* Athlète */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">1 · ATHLÈTE</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Prénom *</Label><Input value={form.athlete_first_name} onChange={(e) => setForm({ ...form, athlete_first_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="athlete-first-name" /></div>
            <div><Label>Nom *</Label><Input value={form.athlete_last_name} onChange={(e) => setForm({ ...form, athlete_last_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="athlete-last-name" /></div>
            <div><Label>Date de naissance *</Label><Input type="date" value={form.athlete_dob} onChange={(e) => setForm({ ...form, athlete_dob: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="athlete-dob" /></div>
            <div><Label>Poids (kg)</Label><Input type="number" step="0.5" value={form.athlete_weight_kg} onChange={(e) => setForm({ ...form, athlete_weight_kg: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="athlete-weight" /></div>
          </div>
        </section>

        {/* École */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">2 · ÉCOLE / DOJO</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Nom de l'école *</Label><Input list="jm-public-schools" value={form.school_name} onChange={(e) => setForm({ ...form, school_name: e.target.value })} placeholder="Institut Impact Val-d'Or" className="bg-zinc-950 border-zinc-800" data-testid="school-name" /><datalist id="jm-public-schools">{refSchools.map((s) => <option key={s.name} value={s.name}>{s.city}</option>)}</datalist></div>
            <div><Label>Ville</Label><Input value={form.school_city} onChange={(e) => setForm({ ...form, school_city: e.target.value })} className="bg-zinc-950 border-zinc-800" /></div>
            <div className="md:col-span-2"><Label>Nom du coach</Label><Input value={form.coach_name} onChange={(e) => setForm({ ...form, coach_name: e.target.value })} className="bg-zinc-950 border-zinc-800" /></div>
          </div>
        </section>

        {/* Disciplines */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">3 · DISCIPLINES (multi-choix) *</div>
          <div className="space-y-3">
            {event.disciplines.map((d) => {
              const entry = form.entries.find((e) => e.discipline === d);
              const isOn = !!entry;
              return (
                <div key={d} className={`border rounded-xl p-4 transition ${isOn ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-950/40"}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={isOn} onChange={() => toggleEntry(d)} className="accent-amber-400" data-testid={`discipline-toggle-${d}`} />
                    <span className="font-semibold text-zinc-100">{DISCIPLINE_LABEL[d]}</span>
                  </label>
                  {isOn && d === "petits_samourais" && (
                    <p className="mt-2 text-xs text-zinc-400" data-testid="petits-samourais-note">Parcours d'habiletés accompagné (parent ou coach) — sans combat, sans pesée, sans ceinture. Tous les enfants repartent avec leur médaille.</p>
                  )}
                  {isOn && d !== "petits_samourais" && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Ceinture / niveau *</Label>
                        <select
                          value={entry.belt_level}
                          onChange={(e) => setBelt(d, e.target.value)}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-2 py-2 text-sm"
                          data-testid={`belt-select-${d}`}
                        >
                          {(BELT_OPTIONS[d] || ["blanche"]).map((b) => (
                            <option key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Parent */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">4 · PARENT / TUTEUR *</div>
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Nom complet *</Label><Input value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="parent-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="bg-zinc-950 border-zinc-800" data-testid="parent-email" /></div>
            <div><Label>Téléphone *</Label><Input value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} placeholder="+1 819 555 1234" className="bg-zinc-950 border-zinc-800" data-testid="parent-phone" /></div>
          </div>
        </section>

        {/* Medical */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">5 · INFO MÉDICALE & URGENCE</div>
          <div className="space-y-3">
            <Textarea value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} placeholder="Allergies, blessures, médicaments…" className="bg-zinc-950 border-zinc-800 min-h-[60px]" />
            <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} placeholder="Contact d'urgence (nom + téléphone)" className="bg-zinc-950 border-zinc-800" />
          </div>
        </section>

        {/* Waiver */}
        <section>
          <div className="text-[10px] tracking-[0.3em] text-amber-400 mb-3">6 · DÉCHARGE DE RESPONSABILITÉ *</div>
          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 text-xs text-zinc-400 max-h-40 overflow-y-auto whitespace-pre-wrap">
            {event.waiver_text || "Le parent/tuteur accepte les risques inhérents aux arts martiaux compétitifs."}
          </div>
          <div className="mt-3 space-y-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.waiver_accepted} onChange={(e) => setForm({ ...form, waiver_accepted: e.target.checked })} className="accent-amber-400" data-testid="waiver-checkbox" />
              <span>J'accepte la décharge de responsabilité au nom de mon enfant.</span>
            </label>
            <div>
              <Label>Signature (taper votre nom complet) *</Label>
              <Input value={form.waiver_signature_text} onChange={(e) => setForm({ ...form, waiver_signature_text: e.target.value })} placeholder="Tape ton nom comme signature" className="bg-zinc-950 border-zinc-800" data-testid="waiver-signature" />
            </div>
          </div>
        </section>

        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full h-14 bg-amber-500 hover:bg-amber-400 text-black font-bold text-lg"
          data-testid="submit-registration-btn"
        >
          {submitting ? "Envoi…" : `🥋 S'inscrire et payer (${form.entries.length} discipline${form.entries.length > 1 ? "s" : ""} · ${(30 + 10 * Math.max(0, form.entries.length - 1)).toFixed(0)} $ CAD)`}
        </Button>

        <div className="text-center text-[11px] text-zinc-700 pb-6 border-t border-zinc-900 pt-6">
          KAIZEN System™ · Réseau franchises · Compétition certifiée
        </div>
      </div>
      )}
    </div>
  );
}
